import { useState, useEffect } from "react";
import axios from "axios";
import { Shield, AlertTriangle, Lock, UserX, Activity, CheckCircle, XCircle, Clock, Eye, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SecurityDashboard = () => {
  const { user } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [securityEvents, setSecurityEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [eventFilter, setEventFilter] = useState("all");

  useEffect(() => {
    fetchSecurityData();
  }, []);

  const fetchSecurityData = async () => {
    try {
      const [dashRes, auditRes, eventsRes] = await Promise.all([
        axios.get(`${API_URL}/security/dashboard`),
        axios.get(`${API_URL}/audit-logs?limit=50`),
        axios.get(`${API_URL}/security/events?limit=50`)
      ]);
      
      setDashboard(dashRes.data);
      setAuditLogs(auditRes.data);
      setSecurityEvents(eventsRes.data);
    } catch (error) {
      toast.error("Failed to fetch security data");
    } finally {
      setLoading(false);
    }
  };

  const getSeverityBadge = (severity) => {
    const styles = {
      low: 'bg-green-100 text-green-800',
      medium: 'bg-yellow-100 text-yellow-800',
      high: 'bg-red-100 text-red-800'
    };
    return (
      <span className={`px-2 py-1 text-xs font-bold uppercase rounded-sm ${styles[severity] || styles.low}`}>
        {severity}
      </span>
    );
  };

  const formatTimestamp = (ts) => {
    return new Date(ts).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredEvents = securityEvents.filter(e => {
    if (eventFilter === "all") return true;
    if (eventFilter === "failed") return !e.success;
    if (eventFilter === "success") return e.success;
    if (eventFilter === "high") return e.severity === "high";
    return true;
  });

  if (loading) {
    return (
      <div className="p-8">
        <div className="text-slate-500">Loading security dashboard...</div>
      </div>
    );
  }

  if (user?.role !== 'admin') {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 rounded-sm p-6 text-center">
          <Lock className="mx-auto text-red-500 mb-3" size={48} />
          <h2 className="text-xl font-bold text-red-800">Access Denied</h2>
          <p className="text-red-600 mt-2">Only administrators can access the Security Dashboard.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="security-dashboard">
      <div className="mb-6 md:mb-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-4xl font-black text-slate-900 uppercase page-title" style={{ letterSpacing: '-0.02em' }}>
              Security Dashboard
            </h1>
            <p className="text-slate-500 mt-1 text-sm md:text-base">Monitor security events and audit logs</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-sm font-bold">
              {dashboard?.encryption_status}
            </span>
            <Button onClick={fetchSecurityData} variant="outline" size="sm">
              <RefreshCw size={16} className="mr-2" /> Refresh
            </Button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 md:gap-4 mb-6">
        <div className="bg-white border border-slate-200 rounded-sm p-4">
          <div className="flex items-center gap-2 mb-2">
            <Activity className="text-slate-500" size={16} />
            <p className="text-xs text-slate-500 font-bold uppercase">Events (24h)</p>
          </div>
          <p className="text-2xl font-bold text-slate-900">{dashboard?.summary?.total_events_24h || 0}</p>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-sm p-4">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle className="text-green-600" size={16} />
            <p className="text-xs text-green-700 font-bold uppercase">Logins (24h)</p>
          </div>
          <p className="text-2xl font-bold text-green-700">{dashboard?.summary?.successful_logins_24h || 0}</p>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-sm p-4">
          <div className="flex items-center gap-2 mb-2">
            <XCircle className="text-red-600" size={16} />
            <p className="text-xs text-red-700 font-bold uppercase">Failed (24h)</p>
          </div>
          <p className="text-2xl font-bold text-red-700">{dashboard?.summary?.failed_logins_24h || 0}</p>
        </div>
        <div className="bg-orange-50 border border-orange-200 rounded-sm p-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="text-orange-600" size={16} />
            <p className="text-xs text-orange-700 font-bold uppercase">High Severity</p>
          </div>
          <p className="text-2xl font-bold text-orange-700">{dashboard?.summary?.high_severity_24h || 0}</p>
        </div>
        <div className="bg-purple-50 border border-purple-200 rounded-sm p-4 col-span-2 lg:col-span-1">
          <div className="flex items-center gap-2 mb-2">
            <Lock className="text-purple-600" size={16} />
            <p className="text-xs text-purple-700 font-bold uppercase">Locked</p>
          </div>
          <p className="text-2xl font-bold text-purple-700">{dashboard?.summary?.locked_accounts || 0}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-6 border-b border-slate-200 pb-4">
        {['overview', 'events', 'audit', 'alerts'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 text-sm font-bold uppercase rounded-sm transition-colors ${
              activeTab === tab 
                ? 'bg-slate-900 text-white' 
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === "overview" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Audit Actions Chart */}
          <div className="bg-white border border-slate-200 rounded-sm">
            <div className="p-4 border-b border-slate-200">
              <h3 className="font-bold text-slate-900 uppercase text-sm">Audit Actions (7 days)</h3>
            </div>
            <div className="p-4 space-y-3 max-h-[300px] overflow-y-auto">
              {dashboard?.audit_by_action?.length === 0 ? (
                <p className="text-slate-500 text-center py-4">No audit data available</p>
              ) : (
                dashboard?.audit_by_action?.map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between">
                    <span className="text-sm text-slate-700 truncate flex-1">{item.action}</span>
                    <span className="font-bold text-slate-900 ml-2">{item.count}</span>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Failed Login Attempts */}
          <div className="bg-white border border-slate-200 rounded-sm">
            <div className="p-4 border-b border-slate-200">
              <h3 className="font-bold text-slate-900 uppercase text-sm">Failed Login Attempts (24h)</h3>
            </div>
            <div className="p-4 space-y-3 max-h-[300px] overflow-y-auto">
              {dashboard?.failed_by_email?.length === 0 ? (
                <p className="text-slate-500 text-center py-4">No failed logins</p>
              ) : (
                dashboard?.failed_by_email?.map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 bg-red-50 rounded-sm">
                    <span className="text-sm text-slate-700 truncate flex-1">{item.email}</span>
                    <span className="font-bold text-red-600 ml-2">{item.count} attempts</span>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Locked Accounts */}
          {dashboard?.locked_accounts?.length > 0 && (
            <div className="bg-white border-2 border-red-200 rounded-sm lg:col-span-2">
              <div className="p-4 border-b border-red-200 bg-red-50">
                <h3 className="font-bold text-red-800 uppercase text-sm flex items-center gap-2">
                  <UserX size={18} /> Currently Locked Accounts
                </h3>
              </div>
              <div className="p-4">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 font-bold text-slate-600">Email</th>
                        <th className="text-center py-2 font-bold text-slate-600">Attempts</th>
                        <th className="text-right py-2 font-bold text-slate-600">Locked Until</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dashboard.locked_accounts.map((acc, idx) => (
                        <tr key={idx} className="border-b">
                          <td className="py-2 text-slate-900">{acc.email}</td>
                          <td className="py-2 text-center font-bold text-red-600">{acc.attempts}</td>
                          <td className="py-2 text-right text-slate-600">{formatTimestamp(acc.locked_until)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Security Events Tab */}
      {activeTab === "events" && (
        <div className="bg-white border border-slate-200 rounded-sm">
          <div className="p-4 border-b border-slate-200 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <h3 className="font-bold text-slate-900 uppercase text-sm">Security Events</h3>
            <Select value={eventFilter} onValueChange={setEventFilter}>
              <SelectTrigger className="w-full md:w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">All Events</SelectItem>
                <SelectItem value="failed">Failed Only</SelectItem>
                <SelectItem value="success">Success Only</SelectItem>
                <SelectItem value="high">High Severity</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-100">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-bold text-slate-600 uppercase">Time</th>
                  <th className="text-left px-4 py-3 text-xs font-bold text-slate-600 uppercase">Event</th>
                  <th className="text-left px-4 py-3 text-xs font-bold text-slate-600 uppercase">Email</th>
                  <th className="text-center px-4 py-3 text-xs font-bold text-slate-600 uppercase">Status</th>
                  <th className="text-center px-4 py-3 text-xs font-bold text-slate-600 uppercase">Severity</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredEvents.map((event) => (
                  <tr key={event.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{formatTimestamp(event.timestamp)}</td>
                    <td className="px-4 py-3 font-medium text-slate-900">{event.event_type}</td>
                    <td className="px-4 py-3 text-slate-700 truncate max-w-[200px]">{event.email}</td>
                    <td className="px-4 py-3 text-center">
                      {event.success ? (
                        <CheckCircle className="inline text-green-600" size={18} />
                      ) : (
                        <XCircle className="inline text-red-600" size={18} />
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">{getSeverityBadge(event.severity)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Audit Logs Tab */}
      {activeTab === "audit" && (
        <div className="bg-white border border-slate-200 rounded-sm">
          <div className="p-4 border-b border-slate-200">
            <h3 className="font-bold text-slate-900 uppercase text-sm">Audit Logs</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-100">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-bold text-slate-600 uppercase">Time</th>
                  <th className="text-left px-4 py-3 text-xs font-bold text-slate-600 uppercase">Action</th>
                  <th className="text-left px-4 py-3 text-xs font-bold text-slate-600 uppercase">User</th>
                  <th className="text-left px-4 py-3 text-xs font-bold text-slate-600 uppercase">Resource</th>
                  <th className="text-left px-4 py-3 text-xs font-bold text-slate-600 uppercase">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {auditLogs.map((log) => (
                  <tr key={log.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{formatTimestamp(log.timestamp)}</td>
                    <td className="px-4 py-3">
                      <span className="font-medium text-slate-900 bg-slate-100 px-2 py-1 rounded-sm text-xs">
                        {log.action}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-700 truncate max-w-[150px]">{log.user_email}</td>
                    <td className="px-4 py-3 text-slate-600">
                      {log.resource_type}/{log.resource_id?.slice(0, 8)}...
                    </td>
                    <td className="px-4 py-3 text-slate-500 text-xs max-w-[200px] truncate">
                      {JSON.stringify(log.details)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Alerts Tab */}
      {activeTab === "alerts" && (
        <div className="bg-white border border-slate-200 rounded-sm">
          <div className="p-4 border-b border-slate-200">
            <h3 className="font-bold text-slate-900 uppercase text-sm">High Severity Alerts</h3>
          </div>
          {dashboard?.high_severity_events?.length === 0 ? (
            <div className="p-8 text-center">
              <Shield className="mx-auto text-green-500 mb-3" size={48} />
              <p className="text-slate-600">No high severity alerts</p>
              <p className="text-xs text-slate-400 mt-1">System is operating normally</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {dashboard?.high_severity_events?.map((event) => (
                <div key={event.id} className="p-4 hover:bg-red-50">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="text-red-600" size={18} />
                        <span className="font-bold text-slate-900">{event.event_type}</span>
                        {getSeverityBadge(event.severity)}
                      </div>
                      <p className="text-sm text-slate-600 mt-1">{event.email}</p>
                      <p className="text-xs text-slate-400 mt-1">{formatTimestamp(event.timestamp)}</p>
                    </div>
                    <Button variant="outline" size="sm">
                      <Eye size={14} className="mr-1" /> Details
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SecurityDashboard;
